import { i18n } from "./i18nService";
import { getSdk } from "./sdkService";
import { checkHistoryProgress } from "./mediaListService";
import { getAppState } from "../state";
import { recheckSeasonsEpisodes, formatSeasonsNumber, getEpisodesData } from "../utils/mediaDataRetrieval";
import { showRetryOrBackModal } from "./modalsService";
import padStart from "../utils/padStart";

const HAS_4K = "4kProgramId";

/**
 * Gets all seasons episodes data and if there's any episode that has already
 * been watched, checks on its progress data and adds it in case it exists. Needs a React
 * component class context since this method is making use of this.state and inner context properties.
 * @param {Array} seasonsList - Array of seasons objects.
 * @returns {Promise}
 * @public
 */
async function getEpisodesList(seasonsList) {
  const {
    match: {
      params: { titleId }
    }
  } = this.props;
  if (!this.state.unAuthorizedAccess) {
    this.setState({ hasEpisodes: this.isSeries });
    return new Promise(async (resolve, reject) => {
      try {
        const episodes = await getEpisodesData(titleId, seasonsList);
        if (episodes && episodes.length) {
          this.episodesList = checkHistoryProgress(
            episodes,
            this.historyList,
            this.watchlist && this.watchlist.length && this.watchlist[0].titles
          );
          resolve(true);
        } else {
          showRetryOrBackModal.call(this);
          reject();
        }
      } catch (e) {
        showRetryOrBackModal.call(this, e);
        reject();
      }
    });
  }
}

/**
 * Checks if detail page title is a series or a movie. Needs a React component class context
 * since this method is making use of inner context properties.
 * @returns {Boolean} Value that determines if detail page title is a series or not.
 * @public
 */
export function isSeriesDetailPage() {
  return this.props.name === "DetailSeries";
}

/**
 * Matches and returns the season and episode number segment from the mediaGuid string.
 * @param mediaGuid - The full mediaGuid string.
 * @return {String} - S00E000 format string.
 * @public
 */
export function getSeasonEpisodeFromMediaGuid(mediaGuid) {
  return mediaGuid.match(/(S\d{2}E\d{3})/i) && mediaGuid.match(/(S\d{2}E\d{3})/i)[0];
}

/**
 * Return the first episode for a given season number in a list.
 * @param {Array} list - The list of episodes.
 * @param {Number} season - The given season number.
 * @return {*} The episode if found.
 * @public
 */
export function findFirstEpisodeBySeason(list, season) {
  const seasonPadded = padStart(season, 2);
  const regex = new RegExp("^S" + seasonPadded + "E\\d+$", "i");
  return list.find(i => regex.test(getSeasonEpisodeFromMediaGuid(i.mediaGuid)));
}

/**
 * Determines if device/content are suitable for reproducing 4K content.
 * @param {Boolean|Object} extraCondition - Extra condition that determines whether the device is suitable
 * for display 4K content or not.
 * @returns {Boolean|Object} - Value that determines if device and content are suitable for 4K playback.
 * @public
 */
export function isSuitableFor4KPlayback(extraCondition) {
  return getAppState().supports4K && extraCondition;
}

/**
 * Gets episode data from last seen episode in case it exists (from watchlist) or
 * from the first episode available in the episodes list. Needs a React
 * component class context since this method is making use of inner context properties.
 * @param {String} property - Field to be found within the episode title data.
 * @public
 */
export function checkEpisodeData(property) {
  const episode = this.lastSeenEpisode || this.episodesList[0];
  return episode[property];
}

/**
 * Checks if title is suitable to be displayed in 4K resolution. If title is a series, it wil look for a
 * $K program ID into either the last watched episode in case there is one, or the first from the episodes list.
 * @returns {Boolean} - Whether the title is suitable for 4K playback or not. Needs a React
 * component class context since this method is making use of inner context properties.
 * @public
 */
export function has4KProgramId() {
  if (this.isSeries) {
    return !!(
      (this.lastSeenEpisode && this.lastSeenEpisode[HAS_4K]) ||
      (this.episodesList && this.episodesList[0] && this.episodesList[0][HAS_4K])
    );
  }
  return !!this.state.has4K;
}

/**
 * Returns the first title trailer data if found.
 * @param {Object} title - Title object.
 * @returns {Object|String} - Object containing trailer media data.
 * @public
 */
export function getTrailer(title) {
  const trailerMediaContent = title.mediaContent.find(mediaItem => {
    return mediaItem.content.trailer;
  });

  if (trailerMediaContent) {
    return trailerMediaContent.content.trailer.hss || trailerMediaContent.content.trailer;
  }
}

/**
 * Gets all available seasons list and returns a label to show in series duration information.
 * Needs a React component class context since this method is making use of this.state and inner context properties.
 * @param {String} titleId - Title ID.
 * @returns {String} - Seasons length label.
 * @public
 */
export async function getSeasonsList(titleId) {
  const sdk = await getSdk();
  try {
    this.seasonsListRaw = await sdk.mediaManager.getSeriesSeasonsList(titleId, !getAppState().isOnline);

    if (this.seasonsListRaw && this.seasonsListRaw.length) {
      this.episodesLoad = getEpisodesList.call(this, this.seasonsListRaw);
      this.episodesLoad.then(() => {
        if (this.episodesList && this.episodesList.length) {
          this.episodesList.forEach(episode => {
            const key = getSeasonEpisodeFromMediaGuid(episode.mediaContent[0].mediaGuid);
            !!this.title4K && !!this.title4K[key] && (episode.title4K = { mediaContent: [this.title4K[key]] });
          });
          (this.seasonsList = recheckSeasonsEpisodes(formatSeasonsNumber(this.seasonsListRaw), this.episodesList)) &&
            this.setState({
              episodeSelectorLoaded: true
            });
        }
      });
      return (
        this.seasonsListRaw.length &&
        `${this.seasonsListRaw.length} ${
          this.seasonsListRaw.length > 1 ? i18n.get("global.media.seasons") : i18n.get("global.media.season")
        }`
      );
    } else {
      !this.state.isPlaying && this.state.unAuthorizedAccess && showRetryOrBackModal.call(this);
    }
  } catch (e) {
    !this.state.isPlaying && this.state.unAuthorizedAccess && showRetryOrBackModal.call(this, e);
  }
}

/**
 * Gets an object containing all the necessary data to be passed and displayed in episode selector
 * page. Needs a React component class context since this method is making use of this.state, this.props
 * and inner context properties.
 * @returns {Object} - Object containing all the data needed for displaying episode selector page.
 * @public
 */
export function getEpisodeSelectorData() {
  const { name, location } = this.props;
  return {
    backgroundCover: this.state.backgroundCover,
    seasonsList: this.seasonsList,
    episodesList: this.episodesList,
    seriesTitle: this.state.title,
    lastEpisode: this.lastSeenEpisode,
    seriesId: this.state.id,
    has4K: this.state.has4K,
    moduleId: location && location.state && location.state.moduleId,
    sectionName:
      (location && location.state && location.state.sectionName) || (name === "DetailMovie" ? "movies" : "series")
  };
}
